"use client";

import Link from "next/link";
import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { HubArrow } from "@/app/_components/conditions/hub/hub-icons";
import { AreaRankClock } from "../AreaRankClock";
import type { AreaPageSport } from "@/lib/areas/types";
import type { AreaSportPanelPayload } from "../area-view-types";
import { AreaAwardsBody, AreaRecordBody } from "./AreaBestTime";
import { AreaPicksCard } from "./AreaPicks";
import { AreaWeekBoard } from "./AreaWeekHeatmap";
import { useAreaSport } from "./area-sport-state";
import { G3_CARD } from "./g3-parts";

/**
 * The on-demand sport panels (AREA-G4 spec item 1): the page server-renders only its own sport's
 * board, picks, record and awards. For every other toggle sport, each of those four places holds
 * one of these islands. When the reader selects the sport (the toggle, an other-sports card, or a
 * shared `?sport=` / `#sport=` link on first paint), the first island to see it fetches the cached
 * panel route once (`/api/conditions/areas/{area}/{sport}/panel?page=`); every island of that
 * sport reads the same in-memory entry and renders the shared, client-safe components from it.
 * A skeleton sized from the server's estimate holds the place while it loads (no page jump); one
 * polite live region (the board island) says loading, loaded or failed; a failed load offers a
 * retry and the sport's full leaderboard, and an empty or failed picks load hides the picks
 * section for that sport.
 * The fetched sport's ranked rows are judged again on the browser's clock (its own AreaRankClock).
 */

export type AreaOnDemandSlot = "board" | "picks" | "record" | "awards";

type Entry =
  | Readonly<{ status: "loading" }>
  | Readonly<{ status: "ready"; payload: AreaSportPanelPayload }>
  | Readonly<{ status: "error" }>;

const entries = new Map<string, Entry>();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function set(url: string, entry: Entry): void {
  entries.set(url, entry);
  for (const listener of listeners) listener();
}

/** The panel route for one sport of one area page. */
export function areaSportPanelUrl(area: string, sport: string, page: AreaPageSport): string {
  return `/api/conditions/areas/${encodeURIComponent(area)}/${encodeURIComponent(sport)}/panel?page=${page}`;
}

function isPayload(value: unknown, sport: string): value is AreaSportPanelPayload {
  if (!value || typeof value !== "object") return false;
  const body = value as Partial<AreaSportPanelPayload>;
  return body.sport === sport && typeof body.panel === "object" && body.panel !== null && typeof body.record === "object" && body.record !== null;
}

/** Starts the one fetch for a panel (a failed one is retried on the next selection). */
export function fetchAreaSportPanel(url: string, sport: string): void {
  const current = entries.get(url);
  if (current && current.status !== "error") return;
  set(url, { status: "loading" });
  fetch(url, { headers: { Accept: "application/json" } })
    .then(async (response) => {
      if (!response.ok) throw new Error(`panel ${response.status}`);
      const body: unknown = await response.json();
      if (!isPayload(body, sport)) throw new Error("panel shape");
      set(url, { status: "ready", payload: body });
    })
    .catch(() => set(url, { status: "error" }));
}

/** Test hook: forget every loaded panel. */
export function resetAreaSportPanels(): void {
  entries.clear();
  for (const listener of listeners) listener();
}

function Bar({ className }: Readonly<{ className: string }>) {
  return <span aria-hidden="true" className={`block animate-pulse rounded-[8px] bg-[#E9ECE7] motion-reduce:animate-none ${className}`} />;
}

/**
 * The server's estimate of a sport's loaded size, so each skeleton holds about the room its
 * content takes (no page jump when it arrives): the board's member count (rows shown: 8 on phones,
 * 10 from md), what sits in the ability row (the surf-family select, the water sports' one-line
 * note, or nothing on mountains), and how many picks the sport can hold.
 */
export type AreaOnDemandSize = Readonly<{ rows: number; ability: "select" | "note" | "none"; picks: number }>;

/**
 * Board skeleton, block for block at the loaded board's measured heights (Cornwall, 3 Oct 2026):
 * phone head 140, chips 60, rows 102 (65 from md) under a 30px day head, footer 126 (80 from md)
 * and legend 105.
 */
function BoardSkeleton({ size }: Readonly<{ size: AreaOnDemandSize }>) {
  if (size.rows === 0) {
    // Nothing ranked today: the loaded board is its head and one empty-state line.
    return (
      <div data-area-skeleton="board" aria-hidden="true">
        <div className="h-[110px] pt-1 md:hidden">
          <Bar className="h-3 w-24" />
          <Bar className="mt-2.5 h-7 w-4/5" />
          <Bar className="mt-2.5 h-10 w-full" />
        </div>
        <div className={`${G3_CARD} mt-3.5 h-[220px] px-3 pt-4 md:mt-0 md:h-[340px] md:px-[22px]`}>
          <Bar className="hidden h-7 w-2/3 md:block" />
          <Bar className="h-16 w-full md:mt-4" />
          <Bar className="mt-4 h-11 w-1/2" />
        </div>
      </div>
    );
  }
  const phoneRows = Math.min(8, Math.max(1, size.rows));
  const deskRows = Math.min(10, Math.max(1, size.rows));
  const ability = size.ability === "select" ? "h-14" : size.ability === "note" ? "h-8 md:h-9" : "h-0";
  return (
    <div data-area-skeleton="board" aria-hidden="true">
      <div className="h-[140px] pt-1 md:hidden">
        <Bar className="h-3 w-24" />
        <Bar className="mt-2.5 h-7 w-4/5" />
        <Bar className="mt-2.5 h-[52px] w-full" />
        <Bar className="mt-2 h-5 w-3/5" />
      </div>
      <div className={`${G3_CARD} mt-3.5 md:mt-0`}>
        <div className="hidden h-[120px] px-[22px] pt-5 md:block">
          <Bar className="h-3 w-24" />
          <Bar className="mt-2 h-7 w-2/3" />
          <Bar className="mt-2 h-10 w-4/5" />
        </div>
        <div className="h-[60px] px-3 pt-3 md:h-[90px] md:px-[18px]"><Bar className="h-11 w-full" /></div>
        <div className={`${ability} px-3 md:px-[18px]`}>{size.ability !== "none" && <Bar className="h-full w-3/4" />}</div>
        <div className="px-2 md:px-3.5">
          <div className="h-[30px]" />
          {Array.from({ length: deskRows }, (_, index) => (
            <div key={index} className={`h-[102px] py-1 md:h-[65px] ${index >= phoneRows ? "hidden md:block" : ""}`}>
              <Bar className="h-full w-full" />
            </div>
          ))}
        </div>
        <div className="h-[126px] px-3 pt-3 md:h-[80px] md:px-[18px]">
          {size.rows > 8 && <Bar className={`mb-2 h-12 w-full md:w-[260px] ${size.rows > 10 ? "" : "md:hidden"}`} />}
          <Bar className="h-11 w-2/3 md:w-1/3" />
        </div>
        <div className="h-[105px] px-3 pt-3 md:px-[18px]"><Bar className="h-[52px] w-full" /></div>
      </div>
    </div>
  );
}

/** Picks skeleton: header, one 111px row per pick (175px cards two to a row from md), the water sports' beginner note, footnote. */
function PicksSkeleton({ size }: Readonly<{ size: AreaOnDemandSize }>) {
  const picks = Math.max(1, size.picks);
  return (
    <div data-area-skeleton="picks" aria-hidden="true" className={`${G3_CARD} pb-4`}>
      <div className="h-[120px] px-4 pt-4 md:h-[88px] md:px-[22px] md:pt-5">
        <Bar className="h-7 w-2/3" />
        <Bar className="mt-2 h-10 w-full md:h-5" />
      </div>
      <div className="md:grid md:grid-cols-2 md:gap-3 md:px-[22px]">
        {Array.from({ length: picks }, (_, index) => (
          <div key={index} className="h-[111px] px-4 py-2 md:h-[175px] md:p-0"><Bar className="h-full w-full" /></div>
        ))}
      </div>
      {size.ability === "note" && <div className="h-[34px] px-4 pt-2 md:h-[30px] md:px-[22px]"><Bar className="h-5 w-full" /></div>}
      <div className="h-[56px] px-4 pt-3 md:h-[46px] md:px-[22px]"><Bar className="h-8 w-full" /></div>
    </div>
  );
}

function RecordSkeleton({ slot }: Readonly<{ slot: "record" | "awards" }>) {
  return (
    <div data-area-skeleton={slot} aria-hidden="true" className={`px-4 py-4 md:px-[22px] md:py-5 ${slot === "record" ? "h-[480px] lg:h-[180px]" : "h-[300px]"}`}>
      <Bar className="h-7 w-2/3" />
      <Bar className="mt-2 h-10 w-full" />
      <div className={`mt-4 flex flex-col gap-2.5 ${slot === "record" ? "lg:hidden" : ""}`}>
        {Array.from({ length: 3 }, (_, index) => <Bar key={index} className="h-14 w-full" />)}
      </div>
    </div>
  );
}

function Skeleton({ slot, size }: Readonly<{ slot: AreaOnDemandSlot; size: AreaOnDemandSize }>) {
  if (slot === "board") return <BoardSkeleton size={size} />;
  // Picks need a ranked board: an unscored sport reserves no room for them.
  if (slot === "picks") return size.rows > 0 ? <PicksSkeleton size={size} /> : null;
  return <RecordSkeleton slot={slot} />;
}

function Failed({ label, boardHref, boardLabel, onRetry }: Readonly<{ label: string; boardHref: string; boardLabel: string; onRetry: () => void }>) {
  return (
    <div role="note" className={`${G3_CARD} px-4 py-4 md:px-[22px] md:py-5`}>
      <p className="text-[15px] font-bold text-navy">{label} could not load just now.</p>
      <p className="mt-1 text-[14px] leading-[1.5] text-[#566072]">Try again, or open its full leaderboard.</p>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex min-h-11 cursor-pointer items-center rounded-[10px] border border-[#BFDDE1] bg-[#F2F8F8] px-4 text-[14.5px] font-bold text-teal-dark"
        >
          Try again
        </button>
        <Link href={boardHref} className="group inline-flex min-h-11 items-center gap-1.5 text-[14.5px] font-bold text-teal-dark underline underline-offset-[3px]">
          {boardLabel}
          <HubArrow />
        </Link>
      </div>
    </div>
  );
}

/** A record or awards slot with nothing to show for the sport: one honest line, never an empty card. */
function Unavailable({ text }: Readonly<{ text: string }>) {
  return <p className="px-4 py-4 text-[14px] leading-[1.5] text-[#566072] md:px-[22px] md:py-5">{text}</p>;
}

function OffSeason({ payload }: Readonly<{ payload: AreaSportPanelPayload }>) {
  const { panel } = payload;
  return (
    <div data-area-resorts={panel.key} className={`${G3_CARD} px-4 py-4 md:px-[22px] md:py-5`}>
      <h2 className="text-[18px] font-bold leading-[1.25] text-navy md:text-[20px]">{panel.label} in {payload.areaName}</h2>
      <p className="mt-1 text-[14px] leading-[1.5] text-[#566072]">
        No {panel.lowerName} scores until the lifts open. {panel.memberCount} {panel.memberCount === 1 ? "resort is" : "resorts are"} scored here once they do.
      </p>
      <Link href={panel.boardHref} className="group mt-2 inline-flex min-h-11 items-center gap-1.5 text-[14.5px] font-bold text-teal-dark underline underline-offset-[3px]">
        {panel.boardLabel}
        <HubArrow />
      </Link>
    </div>
  );
}

function Ready({ slot, payload }: Readonly<{ slot: AreaOnDemandSlot; payload: AreaSportPanelPayload }>) {
  const { panel } = payload;
  const view = { slug: payload.area, name: payload.areaName, kind: payload.kind, timezone: payload.timezone };
  const clock = { [panel.key]: payload.clock };
  switch (slot) {
    case "board":
      if (panel.state === "off-season") return <OffSeason payload={payload} />;
      return <AreaRankClock sports={clock}><AreaWeekBoard panel={panel} view={view} /></AreaRankClock>;
    case "picks":
      return panel.picks ? <AreaRankClock sports={clock}><AreaPicksCard panel={panel} timezone={payload.timezone} /></AreaRankClock> : null;
    case "record":
      return <AreaRecordBody areaName={payload.areaName} kind={payload.kind} unit={panel.unit} bestMonths={payload.record} bestTime={null} awards={payload.awards} />;
    case "awards":
      return payload.awards
        ? <AreaAwardsBody areaName={payload.areaName} unit={panel.unit} awards={payload.awards} />
        : <Unavailable text={`${panel.label} awards could not load just now.`} />;
  }
}

/** The scope root's token list of sports whose picks are empty (CSS hides the picks section and its jump chip). */
export const AREA_EMPTY_PICKS_ATTR = "data-area-empty-picks";

function markEmptyPicks(sport: string, empty: boolean): void {
  const root = document.querySelector<HTMLElement>("[data-area-sport-root]");
  if (!root) return;
  const tokens = new Set((root.getAttribute(AREA_EMPTY_PICKS_ATTR) ?? "").split(" ").filter(Boolean));
  if (empty === tokens.has(sport)) return;
  if (empty) tokens.add(sport); else tokens.delete(sport);
  root.setAttribute(AREA_EMPTY_PICKS_ATTR, [...tokens].join(" "));
}

function loadedMessage(payload: AreaSportPanelPayload): string {
  const { panel } = payload;
  if (panel.state === "off-season") return `${panel.label} loaded: no scores until the lifts open.`;
  const count = panel.heatmap?.totalRows ?? 0;
  const unit = panel.unit === "beaches" ? (count === 1 ? "beach" : "beaches") : count === 1 ? panel.unit.replace(/s$/u, "") : panel.unit;
  return count > 0 ? `${panel.label} loaded, ${count} ${unit} ranked.` : `${panel.label} loaded, no current scores.`;
}

export function AreaSportOnDemand({
  area,
  page,
  sport,
  primary,
  keys,
  slot,
  label,
  boardHref,
  boardLabel,
  size,
}: Readonly<{
  /** The page's area slug. */
  area: string;
  /** The page's own sport (surf or piste): the route groups and ranks the board for that page. */
  page: AreaPageSport;
  /** The toggle sport this island loads. */
  sport: string;
  /** The page's primary toggle key and every toggle key (the shared sport state). */
  primary: string;
  keys: readonly string[];
  slot: AreaOnDemandSlot;
  /** "Prone foil". */
  label: string;
  boardHref: string;
  boardLabel: string;
  /** The skeleton's size (the server knows the member count and the sport's picks). */
  size: AreaOnDemandSize;
}>) {
  const selected = useAreaSport(primary, keys);
  const url = areaSportPanelUrl(area, sport, page);
  const entry = useSyncExternalStore(subscribe, () => entries.get(url) ?? null, () => null);
  const active = selected === sport;
  useEffect(() => {
    if (active) fetchAreaSportPanel(url, sport);
  }, [active, url, sport]);
  // Picks: an empty or failed load hides the picks section (and its jump chip) for this sport.
  const picksEmpty = slot === "picks" && (entry?.status === "error" || (entry?.status === "ready" && !entry.payload.panel.picks));
  useEffect(() => {
    if (slot === "picks") markEmptyPicks(sport, picksEmpty);
  }, [slot, sport, picksEmpty]);
  // One polite live region per sport (the board island): loading, then loaded or failed.
  const message = !active
    ? ""
    : entry?.status === "ready" ? loadedMessage(entry.payload) : entry?.status === "error" ? `${label} could not load.` : `Loading ${label}.`;
  const live = slot === "board" ? <p className="sr-only" role="status" aria-live="polite">{message}</p> : null;
  let body: ReactNode;
  if (entry?.status === "ready") body = <Ready slot={slot} payload={entry.payload} />;
  else if (entry?.status === "error") {
    body = slot === "board"
      ? <Failed label={label} boardHref={boardHref} boardLabel={boardLabel} onRetry={() => fetchAreaSportPanel(url, sport)} />
      : slot === "record" ? <Unavailable text={`The ${label} record could not load just now.`} />
        : slot === "awards" ? <Unavailable text={`${label} awards could not load just now.`} /> : null;
  } else body = <Skeleton slot={slot} size={size} />;
  return <>{live}{body}</>;
}
